import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { FaBan, FaPlus } from "react-icons/fa";
import PropTypes from 'prop-types';
import { Drawer, Button, Alert, FormRow } from 'components/common';
import TinymceEditor from "components/common/TinymceEditor";
import { createChangeOrderRevision, getChangeOrderRevisions } from "redux/crm/project/changeOrderRevisionSlice";

import { INPUT_CLS as inputClass } from 'helpers/formStyles';
import { adjustToNoon } from 'helpers/utils';
const ChangeOrderRevisionForm = ({ showModal, setshowModal, project, building, changeOrder, onCreated }) => {
  const { control, register, handleSubmit, formState: { errors }, setValue } = useForm();
  const dispatch = useDispatch();

  useEffect(() => {
    const revisionNumber = changeOrder?.revision_latest ?? "00";
    const nextNumber = (parseInt(revisionNumber) + 1).toString().padStart(2, '0');

    setValue('rev', nextNumber);
    setValue('entry_date', adjustToNoon(new Date()));
  }, [changeOrder, setValue]);

  const closeModal = () => setshowModal(false);

  const onSubmit = async (data) => {
    try {
      const result = await dispatch(createChangeOrderRevision({
        projectId: project.id,
        buildingId: building.id,
        changeOrderId: changeOrder.id,
        data
      })).unwrap();

      // Refresh the list so the new REV# row appears.
      await dispatch(getChangeOrderRevisions({
        projectId: project.id,
        buildingId: building.id,
        changeOrderId: changeOrder.id
      }));

      closeModal();

      // Open the new revision straight away so the user can add the CO form,
      // PIF and documents changes without an extra click.
      const newId = result?.data?.id;
      if (newId) onCreated?.(newId);
    }
    catch (error) {
      toast.error(error?.error || error?.message || 'Failed to create revision.');
    }
  };

  return (
    <Drawer
      show={showModal}
      onHide={closeModal}
      title={`Make a new Revision for CO#${changeOrder?.co}`}
      width="640px"
      backdropClick={false}
      footer={
        <>
          <Button size="sm" variant="ghost" leftIcon={FaBan} onClick={closeModal}>Cancel</Button>
          <Button size="sm" variant="primary" leftIcon={FaPlus} onClick={handleSubmit(onSubmit)}>Create</Button>
        </>
      }
    >
      <Alert type="warning" className="tw-mb-4">
        Create a new revision for CO#{changeOrder?.co}. Documents, PIF and CO form changes are managed on the
        revision detail page after creation — the list of changes is generated automatically from those.
      </Alert>

      <FormRow label="REV#" error={errors.rev?.message}>
        <input
          type="text"
          placeholder="REV#"
          className={inputClass}
          disabled
          {...register("rev", { required: "REV# must be required." })}
        />
      </FormRow>

      <FormRow label="Sales note" error={errors.sales_note?.message}>
        <Controller
          control={control}
          name="sales_note"
          render={({ field }) => (
            <TinymceEditor handleChange={field.onChange} value={field.value} />
          )}
        />
      </FormRow>
    </Drawer>
  );
};

ChangeOrderRevisionForm.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setshowModal: PropTypes.func.isRequired,
  project: PropTypes.object,
  building: PropTypes.object,
  changeOrder: PropTypes.object,
  onCreated: PropTypes.func
};

export default ChangeOrderRevisionForm;
